<template>
  <div id="map-container">
    <div
      id="map"
      class="map"
    >
      <ColorBar
        v-if="colorBarVisible"
        v-model="colorbarScale"
        :gradient="gradient"
        style="z-index: 1"
        @colorbar-slider-change="onUpdateColorbarSlider"
        @setAutoAdjust="onUpdateAutoAdjust"
      />
      <BottomControlsNavBar
        style="z-index: 1"
        :point-layer-types="pointLayerTypes"
      />
      <SelectedPointOverlay
        v-show="selectedPoint"
        :selected-point="selectedPoint"
      />
    </div>
  </div>
</template>

<script>
"use strict";

import "ol/ol.css";
import "ol-layerswitcher/dist/ol-layerswitcher.css";

import { mapGetters } from "vuex";

import { Map, View } from "ol";
import Stamen from "ol/source/Stamen";
import OSM from "ol/source/OSM";
import TileLayer from "ol/layer/Tile";
// import { fromLonLat, toLonLat, transform } from "ol/proj.js";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import LayerGroup from "ol/layer/Group";
import Heatmap from "ol/layer/Heatmap";
import GeoJSON from "ol/format/GeoJSON";
import { transform } from "ol/proj.js";
import { Style, Fill, Text, Stroke, Circle } from "ol/style";
// import { Control, defaults as defaultControls } from "ol/control";
// import {ScaleLine, defaults as defaultControls} from 'ol/control';
import { ScaleLine } from "ol/control";
import LayerSwitcher from "ol-layerswitcher";
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
// import { BaseLayerOptions, GroupLayerOptions } from "ol-layerswitcher";

import ColorBar from "@/components/ColorBar";
import BottomControlsNavBar from "@/components/bottomControls/BottomControlsNavBar";
import SelectedPointOverlay from "@/components/mapOverlays/SelectedPointOverlay";

import PointLayerHighlighting from "@/mixins/map/PointLayerHighlighting";

export default {
  name: "Map",
  components: { ColorBar, BottomControlsNavBar, SelectedPointOverlay },
  mixins: [PointLayerHighlighting],
  props: {
    updateId: String,
    agsId: String,
    pointLayerTypes: Array,
    geoInputTypes: Array
  },
  data() {
    return {
      map: null,
      sourceProjection: "EPSG:4326",
      targetProjection: "EPSG:3857",
      coords: null,

      tileLayerGroup: null,
      heatMapLayerGroup: null,
      pointLayerGroup: null,
      geoInputLayerGroup: null,

      colorBarVisible: false,
      gradient: [
        [0, [255, 255, 255, 0.5]],
        [25, [0, 0, 255, 0.7]],
        [50, [0, 255, 0, 0.7]],
        [75, [255, 255, 0, 0.7]],
        [100, [255, 0, 0, 0.7]]
      ],
      colorbarScale: { min: 0, max: 0 },
      colorbarSliderValue: 0,
      autoAdjust: true,
      currentTileLayerMaxValue: 0
    };
  },
  computed: {
    ...mapGetters(["ags"])
  },
  watch: {
    updateId: function() {
      this.hideAllUpdateLayers();
    },
    agsId: function() {
      // hide all AGS-specific layers (currently: all geoInput layers)
      this.hideGeoInputLayers();
      window.EventBus.$emit("deselectGeoInputLayers");
      // Set view to AGS geoJSON.
      if (this.ags.find(ags => ags.agsId === this.agsId)?.agsGeo) {
        this.map
          .getView()
          .fit(
            new GeoJSON().readGeometry(
              this.ags.find(ags => ags.agsId === this.agsId).agsGeo.features[0]
                .geometry,
              { featureProjection: this.targetProjection }
            )
          );
      }
    },
    /**
     * Is called when in component ColorBar the minimum value for the color bar is changed.
     */
    "colorbarScale.min": function() {
      this.updateTileLayerStyle();
    },
    /**
     * Is called when in component ColorBar the maximum value for the color bar is changed.
     */
    "colorbarScale.max": function() {
      this.updateTileLayerStyle();
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.mapHeightHandler);
  },
  mounted() {
    window.addEventListener("resize", this.mapHeightHandler);
    this.initMap();
    window.EventBus.$on("showLayer", (layerType, year) =>
      this.showLayer(layerType, year)
    );
    window.EventBus.$on("hideTileLayers", () => this.hideTileLayers());
    window.EventBus.$on("hideHeatMapLayers", () => this.hideHeatMapLayers());
    window.EventBus.$on("hidePointLayers", () => this.hidePointLayers());
    window.EventBus.$on("hideGeoInputLayers", () => this.hideGeoInputLayers());

    this.map.on("pointermove", this.highlightOnPointerMove);
    window.EventBus.$emit("showBottomControlButton", "Screenshot");

    window.EventBus.$on("resizeMap", () => this.mapHeightHandler());
  },
  methods: {
    initMap() {
      // Create map object.
      this.map = new Map({
        layers: [
          new TileLayer({
            id: "OSM",
            title: "OSM",
            type: "base",
            source: new OSM()
          }),
          new TileLayer({
            id: "toner",
            title: "Stamen",
            type: "base",
            source: new Stamen({
              layer: "toner"
            }),
            visible: false
          })
        ],
        target: "map",
        view: new View({
          center: transform(
            [10, 51],
            this.sourceProjection,
            this.targetProjection
          ),
          zoom: 6,
          zoomFactor: 2
        }),
        overlays: [this.selectedPointPopupOverlay]
      });

      this.map.addInteraction(new MouseWheelZoom({
        maxDelta: 0.01,
        timeout: 200,
        constrainResolution: true
      }));

      const layerSwitcher = new LayerSwitcher({
        reverse: false
      });

      this.map.addControl(layerSwitcher);

      var scaleLine = new ScaleLine({ bar: true, text: true, minWidth: 125 });
      this.map.addControl(scaleLine);

      this.map.updateSize();

      var self = this;
      this.map.on("singleclick", function(e) {
        self.coords = e.coordinate;
      });

      // map.on("pointermove", function(evt) {
      //   if (evt.dragging) {
      //     return;
      //   }
      //   var pixel = map.getEventPixel(evt.originalEvent);
      //   displayFeatureInfo(pixel);
      // });
      // this.map.on("click", function(evt) {
      //   displayFeatureInfo(evt.pixel);
      // });

      // toggle point selection with click
      this.map.on("click", this.togglePointSelect);

      // Add layer group for tile maps. It is used to scroll through the years.
      this.tileLayerGroup = new LayerGroup({ type: "tile" });
      this.map.addLayer(this.tileLayerGroup);

      // Add layer group for heatmaps. It is used to scroll through the years.
      this.heatMapLayerGroup = new LayerGroup({ type: "heatmap" });
      this.map.addLayer(this.heatMapLayerGroup);

      // Add layer group for point maps. It is used to enable multiple point map layers independently.
      this.pointLayerGroup = new LayerGroup({ type: "point" });
      this.map.addLayer(this.pointLayerGroup);

      // Add layer group for point/vector geo input maps. It is used to enable multiple geo input map layers independently.
      this.geoInputLayerGroup = new LayerGroup();
      this.map.addLayer(this.geoInputLayerGroup);
    },
    getLayer(updateId, layerTypeId, year) {
      var self = this;
      $api.get(`${window.actionUrl}layer/${updateId}/${layerTypeId}/${year}`)
        .then(function(res) {
          if (!res.data.error) {
            const layer = res.data.layer[0];
            if (layer.layerClass === "area") {
              // Tile maps.
              // Get maximum value in that layer.
              let maxValue = Math.max(
                ...layer.geo.features.map(feature => feature.properties.data)
              );
              self.currentTileLayerMaxValue = maxValue;
              if (self.autoAdjust) {
                self.colorbarScale.max = maxValue;
              }
              const tileLayer = new VectorLayer({
                year: layer.year,
                description: layer.description,
                name: layer.name,
                layerTypeId: layer.layertypesId,
                updateId: updateId,
                maxDataValue: maxValue, // store the max data value within the layer
                source: new VectorSource({
                  features: new GeoJSON().readFeatures(layer.geo, {
                    featureProjection: self.targetProjection
                  })
                }),
                style: self.tileLayerStyler
              });
              // Check if layer group for this layer type ID already exists.
              let lg = self.tileLayerGroup
                .getLayers()
                .getArray()
                .find(
                  layerGroup =>
                    layerGroup.get("layerTypeId") == layer.layertypesId &&
                    layerGroup.get("updateId") == updateId
                );
              // Create layer group if it doesn't exist.
              if (!lg) {
                lg = new LayerGroup({
                  layerTypeId: layer.layertypesId,
                  updateId: updateId
                });
                // Add layer group to the tile layer group.
                self.tileLayerGroup.getLayers().push(lg);
              }
              // Add new tile layer (year) to layer group.
              lg.getLayers().push(tileLayer);
            } else if (layer.layerClass === "heatmap") {
              // Heatmaps.
              // Get maximum value in that layer.
              let maxValue = Math.max(
                ...layer.geo.features.map(feature => feature.properties.data)
              );
              if (self.autoAdjust) {
                self.colorbarScale.max = maxValue;
              }
              const heatmapLayer = new Heatmap({
                year: layer.year,
                description: layer.description,
                name: layer.name,
                layerTypeId: layer.layertypesId,
                updateId: updateId,
                maxDataValue: maxValue, // store the max data value within the layer
                source: new VectorSource({
                  features: new GeoJSON().readFeatures(layer.geo, {
                    featureProjection: self.targetProjection
                  })
                }),
                weight: feature =>
                  feature.getProperties().data / self.colorbarScale.max
              });
              // Check if layer group for this layer type ID already exists.
              let lg = self.heatmapLayerGroup
                .getLayersArray()
                .find(
                  layerGroup =>
                    layerGroup.get("layerTypeId") == layer.layertypesId &&
                    layerGroup.get("updateId") == updateId
                );
              // Create layer group if it doesn't exist.
              if (!lg) {
                lg = new LayerGroup({
                  id: layer.layertypesId,
                  updateId: updateId
                });
                // Add the layer group to the heatmap layer group.
                self.heatMapLayerGroup.getLayers().push(lg);
              }
              // Add layer group to tile layer group.
              lg.getLayers().push(heatmapLayer);
            } else if (layer.layerClass === "point") {
              // Point maps.
              const vl = new VectorLayer({
                year: layer.year,
                description: layer.description,
                name: layer.name,
                layerTypeId: layer.layertypesId,
                updateId: updateId,
                source: new VectorSource({
                  features: new GeoJSON().readFeatures(layer.geo, {
                    featureProjection: self.targetProjection
                  })
                })
              });
              self.setVectorLayerStyle(
                vl,
                self.pointLayerTypes.find(
                  layerType => layerType.layertypesId == layer.layertypesId
                )
              );
              self.pointLayerGroup.getLayers().push(vl);
              window.EventBus.$emit("showPointLayerInTable", vl);
            }
          }
        });
    },
    getGeoInputLayer(geoInputId) {
      var self = this;
      $api.get(`${window.actionUrl}geoInput/${geoInputId}`)
        .then(function(res) {
          if (!res.data.error) {
            const layer = res.data.geoInput[0];
            const vl = new VectorLayer({
              geoInputId: geoInputId,
              source: new VectorSource({
                features: new GeoJSON().readFeatures(layer.geoInput, {
                  featureProjection: self.targetProjection
                })
              })
            });
            self.setVectorLayerStyle(
              vl,
              self.geoInputTypes.find(
                geoInputType => geoInputType.geoInputId == geoInputId
              )
            );
            self.geoInputLayerGroup.getLayers().push(vl);
          }
        });
    },
    showLayer(layerType, year) {
      if (layerType.layerClass === "area") {
        // Check if there is already an existing tile layer group for the given layer typ ID.
        const existingTileLayerGroup = this.tileLayerGroup
          .getLayers()
          .getArray()
          .find(
            layerGroup =>
              layerGroup.get("layerTypeId") == layerType.layertypesId &&
              layerGroup.get("updateId") == this.updateId
          );
        if (!existingTileLayerGroup) {
          // If there is no group -> there is no layer -> get it and create it together with the group layer it belongs to.
          this.getLayer(this.updateId, layerType.layertypesId, year);
        } else {
          // If the tile layer group for the given layer type ID and update ID already exists look for tile layer with given year.
          const existingLayer = existingTileLayerGroup
            .getLayers()
            .getArray()
            .find(layer => layer.get("year") == year);
          if (!existingLayer) {
            // If there is no layer -> create it.
            this.getLayer(this.updateId, layerType.layertypesId, year);
          } else {
            // If the layer already exists -> make it visible
            // and set the normalizer value for the color bar scale if autoAdjust is set.
            this.currentTileLayerMaxValue = existingLayer.get("maxDataValue");
            if (this.autoAdjust) {
              this.colorbarScale.max = existingLayer.get("maxDataValue");
            }
            existingLayer.setVisible(true);
          }
        }
        this.colorBarVisible = true;
      } else if (layerType.layerClass === "heatmap") {
        // Check if there is already an existing heat map layer group for the given layer typ ID.
        const existingHeatMapLayerGroup = this.heatMapLayerGroup
          .getLayers()
          .getArray()
          .find(
            layerGroup =>
              layerGroup.get("layerTypeId") == layerType.layertypesId &&
              layerGroup.get("updateId") == this.updateId
          );
        if (!existingHeatMapLayerGroup) {
          // If there is no group -> there is no layer -> get it and create it together with the group layer it belongs to.
          this.getLayer(this.updateId, layerType.layertypesId, year);
        } else {
          // If the heat map layer group for the given layer type ID and update ID already exists look for heat map layer with given year.
          const existingLayer = existingHeatMapLayerGroup
            .getLayers()
            .getArray()
            .find(layer => layer.get("year") == year);
          if (!existingLayer) {
            // If there is no layer -> create it.
            this.getLayer(this.updateId, layerType.layertypesId, year);
          } else {
            // If the layer already exists -> make it visible
            // and set the normalizer value for the color bar scale if autoAdjust is set.
            if (this.autoAdjust) {
              this.colorbarScale.max = existingLayer.get("maxDataValue");
            }
            existingLayer.setVisible(true);
          }
        }
      } else if (layerType.layerClass === "point") {
        window.EventBus.$emit(
          "showBottomControlButton",
          "SelectedPointLayersInfoTable"
        );
        // Get already existing layers.
        const existingLayer = this.pointLayerGroup
          .getLayers()
          .getArray()
          .find(
            layer =>
              layer.get("layerTypeId") == layerType.layertypesId &&
              layer.get("updateId") == this.updateId &&
              layer.get("year") == year
          );
        if (!existingLayer) {
          this.getLayer(this.updateId, layerType.layertypesId, year);
        } else {
          // Turn the selected layer's visibility on.
          existingLayer.setVisible(true);
          window.EventBus.$emit("showPointLayerInTable", existingLayer);
        }
      } else if (layerType.layerClass === "geoInput") {
        const existingLayer = this.geoInputLayerGroup
          .getLayers()
          .getArray()
          .find(layer => layer.get("geoInputId") == layerType.geoInputId);
        if (!existingLayer) {
          this.getGeoInputLayer(layerType.geoInputId);
        } else {
          existingLayer.setVisible(true);
        }
      }
    },
    tileLayerStyler(feature) {
      let normalizer = this.colorbarScale.max - this.colorbarScale.min;
      const featureIntensity =
        ((feature.getProperties().data - this.colorbarScale.min) / normalizer) *
        100;

      var colorRange = [];
      if (featureIntensity <= 0) {
        colorRange = [0, 0];
      } else if (featureIntensity > 100) {
        colorRange = [4, 4];
      } else {
        let gradientLength = this.gradient.length;
        let i = 1;
        for (; i < gradientLength; i++) {
          if (featureIntensity <= this.gradient[i][0]) {
            colorRange = [i - 1, i];
            break;
          }
        }
      }

      // Get the two closest colors.
      var firstColor = this.gradient[colorRange[0]][1];
      var secondColor = this.gradient[colorRange[1]][1];

      // Calculate ratio between the two colors.
      let firstColorX = this.gradient[colorRange[0]][0];
      let secondColorX = this.gradient[colorRange[1]][0];
      let distanceBetweenColors = secondColorX - firstColorX;
      let distanceToFirstColor = Math.abs(featureIntensity - firstColorX);
      let distanceToSecondColor = Math.abs(featureIntensity - secondColorX);
      let firstColorWeight = 0.5;
      let secondColorWeight = 0.5;
      if (distanceBetweenColors != 0) {
        firstColorWeight = 1 - distanceToFirstColor / distanceBetweenColors;
        secondColorWeight = 1 - distanceToSecondColor / distanceBetweenColors;
      }

      let rgba = [
        Math.round(
          firstColor[0] * firstColorWeight + secondColor[0] * secondColorWeight
        ),
        Math.round(
          firstColor[1] * firstColorWeight + secondColor[1] * secondColorWeight
        ),
        Math.round(
          firstColor[2] * firstColorWeight + secondColor[2] * secondColorWeight
        ),
        // Determine the opacity value depending on the colorbar slider values.
        // featureIntensity <= this.colorbarSliderValue[1] &&
        featureIntensity >= this.colorbarSliderValue
          ? firstColor[3] * firstColorWeight +
            secondColor[3] * secondColorWeight
          : 0
      ];

      const style = new Style({
        fill: new Fill({
          color: [...rgba]
        })
      });

      return style;
    },
    hideAllUpdateLayers() {
      // Make all currently displayed update-specific layers invisible.
      // Tile layers.
      this.hideTileLayers();
      // Heat map layers.
      this.hideHeatMapLayers();
      // Point layers.
      this.hidePointLayers();
    },
    /**
     * Set all tile layers visibility to false.
     */
    hideTileLayers() {
      this.colorBarVisible = false;
      this.tileLayerGroup
        .getLayers()
        .getArray()
        .forEach(layerGroup => {
          layerGroup
            .getLayers()
            .getArray()
            .forEach(layer => layer.setVisible(false));
        });
    },
    /**
     * Set all heat map layers visibility to false.
     */
    hideHeatMapLayers() {
      this.heatMapLayerGroup
        .getLayers()
        .getArray()
        .forEach(layerGroup => {
          layerGroup
            .getLayers()
            .getArray()
            .forEach(layer => layer.setVisible(false));
        });
    },
    /**
     * Set all point layers visibility to false.
     */
    hidePointLayers() {
      this.pointLayerGroup
        .getLayers()
        .getArray()
        .forEach(layer => {
          layer.setVisible(false);
        });
      window.EventBus.$emit(
        "hideBottomControlButton",
        "SelectedPointLayersInfoTable"
      );
    },
    /**
     * Set all geo input layers visibility to false.
     */
    hideGeoInputLayers() {
      this.geoInputLayerGroup
        .getLayers()
        .getArray()
        .forEach(layer => {
          layer.setVisible(false);
        });
    },
    // displayFeatureInfo(pixel) {
    //   var features = [];
    //   map.forEachFeatureAtPixel(pixel, function(feature) {
    //     features.push(feature);
    //   });
    //   if (features.length > 0) {
    //     var info = [];
    //     var i, ii;
    //     for (i = 0, ii = features.length; i < ii; ++i) {
    //       info.push(features[i].get("desc"));
    //     }
    //     document.getElementById("info").innerHTML =
    //       info.join(", ") || "(unknown)";
    //     map.getTarget().style.cursor = "pointer";
    //   } else {
    //     document.getElementById("info").innerHTML = "&nbsp;";
    //     map.getTarget().style.cursor = "";
    //   }
    // },
    mapHeightHandler() {
      let distanceToTop =
        window.pageYOffset +
        document.getElementById("map").getBoundingClientRect().top;
      let mapHeight = window.innerHeight - distanceToTop;
      document.getElementById("map").style.height = mapHeight + "px";
      this.map.updateSize();
    },
    /**
     * Is called when in component ColorBar the sliders are changed.
     * Then the colorbarSliderValue is changed accordingly.
     * After changing this value the style of the current tile layer is updated accordingly.
     */
    onUpdateColorbarSlider(sliderValue) {
      this.colorbarSliderValue = sliderValue;
      this.updateTileLayerStyle();
    },
    /**
     * Is called when auto adjust is changed in component ColorBar so the value is also usable in this component.
     */
    onUpdateAutoAdjust(newAutoAdjustValue) {
      this.autoAdjust = newAutoAdjustValue;
      if (newAutoAdjustValue) {
        this.colorbarScale.min = 0;
        this.colorbarScale.max = this.currentTileLayerMaxValue;
      }
    },
    /**
     * Set vector layer style (color and icon) based on layerType.
     * Used for point- and geoInput-layers.
     */
    setVectorLayerStyle(layer, layerType) {
      if (layerType.icon) {
        // set custom icons/colors
        layer.setStyle(
          new Style({
            text: new Text({
              text: layerType.icon.unicode,
              font: '900 16px "Font Awesome 5 Free"',
              fill: new Fill({ color: layerType.color })
            })
          })
        );
      } else {
        // set default style with custom colors
        let stroke = new Stroke({
          color: layerType.color,
          width: 2
        });
        let fill = new Fill({
          color: "rgba(255,255,255,0.4)"
        });
        layer.setStyle(
          new Style({
            image: new Circle({
              fill: fill,
              stroke: stroke,
              radius: 5
            }),
            fill: fill,
            stroke: stroke
          })
        );
      }
    },
    /**
     * Recreate style for currently displayed layers.
     */
    updateTileLayerStyle() {
      {
        this.tileLayerGroup
          .getLayers()
          .getArray()
          .forEach(layerGroup => {
            layerGroup
              .getLayers()
              .getArray()
              .forEach(layer => layer.setStyle(this.tileLayerStyler));
          });
      }
    }
  }
};
</script>

<style>
#map-container {
  height: 100%;
  width: 100%;
  /* height: 530px; */
  /* width: 500px; */
}
#map {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  /* height: 530px; */
  /* width: 500px; */
}
</style>
